
import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TopPrompt } from "@/services/analytics/metricsService";
import { Skeleton } from "@/components/ui/skeleton";
import { Download } from "lucide-react";

interface TopPromptsTableProps {
  data: TopPrompt[];
  isLoading?: boolean;
}

const TopPromptsTable: React.FC<TopPromptsTableProps> = ({
  data,
  isLoading = false,
}) => {
  const [sortBy, setSortBy] = useState<keyof TopPrompt>("copies");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

  const handleSort = (column: keyof TopPrompt) => {
    if (sortBy === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortDirection("desc");
    }
  };

  const sortedData = [...data].sort((a, b) => {
    if (sortDirection === "asc") {
      return a[sortBy] > b[sortBy] ? 1 : -1;
    } else {
      return a[sortBy] < b[sortBy] ? 1 : -1;
    }
  });

  const exportToCSV = () => {
    const headers = ["Title", "Category", "Copies", "Saves", "Collections", "Rating"];
    const csvContent = [
      headers.join(","),
      ...sortedData.map(item => 
        [
          `"${item.title}"`, 
          `"${item.category}"`, 
          item.copies, 
          item.saves, 
          item.collections, 
          item.rating
        ].join(",")
      )
    ].join("\n");
    
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    
    link.setAttribute("href", url);
    link.setAttribute("download", "top_prompts.csv");
    link.style.visibility = "hidden";
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Card className="col-span-4">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Top Performing Prompts</CardTitle>
        <Button variant="outline" size="sm" onClick={exportToCSV}>
          <Download className="mr-2 h-4 w-4" />
          Export CSV
        </Button>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-2">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead 
                    className="cursor-pointer"
                    onClick={() => handleSort("title")}
                  >
                    Title 
                    {sortBy === "title" && (sortDirection === "asc" ? " ▲" : " ▼")}
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer"
                    onClick={() => handleSort("category")}
                  >
                    Category
                    {sortBy === "category" && (sortDirection === "asc" ? " ▲" : " ▼")}
                  </TableHead>
                  <TableHead 
                    className="text-right cursor-pointer"
                    onClick={() => handleSort("copies")}
                  >
                    Copies
                    {sortBy === "copies" && (sortDirection === "asc" ? " ▲" : " ▼")}
                  </TableHead>
                  <TableHead 
                    className="text-right cursor-pointer"
                    onClick={() => handleSort("saves")}
                  >
                    Saves
                    {sortBy === "saves" && (sortDirection === "asc" ? " ▲" : " ▼")}
                  </TableHead>
                  <TableHead 
                    className="text-right cursor-pointer"
                    onClick={() => handleSort("collections")}
                  >
                    Collections
                    {sortBy === "collections" && (sortDirection === "asc" ? " ▲" : " ▼")}
                  </TableHead>
                  <TableHead 
                    className="text-right cursor-pointer"
                    onClick={() => handleSort("rating")}
                  >
                    Rating
                    {sortBy === "rating" && (sortDirection === "asc" ? " ▲" : " ▼")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedData.map((prompt) => (
                  <TableRow key={prompt.id}>
                    <TableCell className="font-medium">{prompt.title}</TableCell>
                    <TableCell>{prompt.category}</TableCell>
                    <TableCell className="text-right">{prompt.copies}</TableCell>
                    <TableCell className="text-right">{prompt.saves}</TableCell>
                    <TableCell className="text-right">{prompt.collections}</TableCell>
                    <TableCell className="text-right">{prompt.rating}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TopPromptsTable;
